import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import PageCard from "components/admin/PageCard";
import GameIcon from "components/admin/GameIcon";
import { useFetch, useRunner } from "components/admin/hooks";
import { useConfirm } from "components/admin/Feedback";
import useConfirmChanges from "components/admin/confirmChanges";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";

/** Cấp thật: server bốc cấp bằng Random.Next(from, to) nên chặn trên bị loại trừ. */
function effectiveTo(from, to) {
  const f = Number(from);
  const t = Number(to);
  return t > f ? t - 1 : f;
}

function lvlText(from, to) {
  const eff = effectiveTo(from, to);
  const f = Number(from);
  return eff === f ? `cấp ${f}` : `cấp ${f}–${eff}`;
}

/** Một loài quái trên map: sửa khoảng cấp + nguyên mẫu, hoặc xoá khỏi map. */
function MobRow({ row, archetypes, busy, onSave, onDelete }) {
  const canEdit = useCan(PERMISSIONS.RATE_EDIT);
  const [draft, setDraft] = useState({
    lvlFrom: row.lvlFrom,
    lvlTo: row.lvlTo,
    archetypeId: row.archetypeId,
  });
  const dirty =
    Number(draft.lvlFrom) !== row.lvlFrom ||
    Number(draft.lvlTo) !== row.lvlTo ||
    Number(draft.archetypeId) !== row.archetypeId;

  return (
    <MDBox py={0.75}>
      <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <GameIcon path={row.petIcon} size={32} fallback="pets" />
        <MDBox width={170} minWidth={170}>
          <MDTypography variant="caption" color="text" display="block" noWrap>
            #{row.petId} · {row.elementName}
          </MDTypography>
          <MDTypography variant="button" display="block" noWrap>
            {row.petName}
          </MDTypography>
        </MDBox>

        <MDBox width={78}>
          <MDTypography variant="caption" color="text" display="block">
            Cấp từ
          </MDTypography>
          <MDInput
            type="number"
            value={draft.lvlFrom}
            inputProps={{ step: 1 }}
            InputProps={{ readOnly: !canEdit }}
            sx={{ width: "100%" }}
            onChange={(e) => setDraft({ ...draft, lvlFrom: e.target.value })}
          />
        </MDBox>
        <MDBox width={78}>
          <MDTypography variant="caption" color="text" display="block">
            Cấp đến
          </MDTypography>
          <MDInput
            type="number"
            value={draft.lvlTo}
            inputProps={{ step: 1 }}
            InputProps={{ readOnly: !canEdit }}
            sx={{ width: "100%" }}
            onChange={(e) => setDraft({ ...draft, lvlTo: e.target.value })}
          />
        </MDBox>

        <MDBox width={150}>
          <MDTypography variant="caption" color="text" display="block">
            Nguyên mẫu
          </MDTypography>
          <MDInput
            select
            value={draft.archetypeId}
            disabled={!canEdit}
            sx={{ width: "100%" }}
            onChange={(e) => setDraft({ ...draft, archetypeId: e.target.value })}
          >
            {archetypes.map((a) => (
              <MenuItem key={a.archetypeId} value={a.archetypeId}>
                {a.name}
              </MenuItem>
            ))}
          </MDInput>
        </MDBox>

        {canEdit && (
          <>
            <MDButton
              size="small"
              color="info"
              disabled={busy || !dirty}
              onClick={() => onSave(draft)}
            >
              Lưu
            </MDButton>
            <MDButton
              size="small"
              color="error"
              variant="outlined"
              disabled={busy}
              onClick={onDelete}
            >
              Xoá
            </MDButton>
          </>
        )}
      </MDBox>

      <MDBox display="flex" alignItems="center" gap={1} pl={5} flexWrap="wrap">
        <MDTypography variant="caption" color="text">
          Thực tế sinh ra {lvlText(draft.lvlFrom, draft.lvlTo)}
        </MDTypography>
        {!row.frameOk && (
          <Tooltip title={row.frameNote || ""}>
            <Chip
              size="small"
              color="error"
              icon={<Icon>broken_image</Icon>}
              label={`Ảnh lỗi: khung ${row.frameWidth}px`}
            />
          </Tooltip>
        )}
      </MDBox>
    </MDBox>
  );
}

MobRow.propTypes = {
  row: PropTypes.object.isRequired,
  archetypes: PropTypes.array.isRequired,
  busy: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

/** Khung thêm một loài quái mới vào map. */
function AddMob({ mapId, pets, archetypes, busy, onAdd }) {
  const [pet, setPet] = useState(null);
  const [lvlFrom, setLvlFrom] = useState("");
  const [lvlTo, setLvlTo] = useState("");
  const [archetypeId, setArchetypeId] = useState(0);

  const submit = async () => {
    const ok = await onAdd({
      mapId,
      petId: pet.petId,
      lvlFrom: Number(lvlFrom),
      lvlTo: Number(lvlTo),
      archetypeId: Number(archetypeId),
    });
    if (ok) {
      setPet(null);
      setLvlFrom("");
      setLvlTo("");
      setArchetypeId(0);
    }
  };

  return (
    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" pt={1}>
      <Autocomplete
        value={pet}
        options={pets}
        onChange={(e, option) => setPet(option)}
        getOptionLabel={(x) => `#${x.petId} ${x.name}`}
        isOptionEqualToValue={(a, b) => a.petId === b.petId}
        noOptionsText="Không có mẫu phù hợp"
        sx={{ width: 260 }}
        renderOption={(props, option) => (
          <MDBox component="li" {...props} key={option.petId} display="flex" gap={1}>
            <GameIcon path={option.icon} size={26} fallback="pets" />
            <MDTypography variant="button">
              {`#${option.petId} ${option.name} · ${option.elementName}`}
            </MDTypography>
          </MDBox>
        )}
        renderInput={(params) => <MDInput {...params} label="Thêm loài quái" />}
      />
      <MDBox width={78}>
        <MDInput
          type="number"
          label="Cấp từ"
          value={lvlFrom}
          inputProps={{ step: 1 }}
          sx={{ width: "100%" }}
          onChange={(e) => setLvlFrom(e.target.value)}
        />
      </MDBox>
      <MDBox width={78}>
        <MDInput
          type="number"
          label="Cấp đến"
          value={lvlTo}
          inputProps={{ step: 1 }}
          sx={{ width: "100%" }}
          onChange={(e) => setLvlTo(e.target.value)}
        />
      </MDBox>
      <MDBox width={150}>
        <MDInput
          select
          label="Nguyên mẫu"
          value={archetypeId}
          sx={{ width: "100%" }}
          onChange={(e) => setArchetypeId(e.target.value)}
        >
          {archetypes.map((a) => (
            <MenuItem key={a.archetypeId} value={a.archetypeId}>
              {a.name}
            </MenuItem>
          ))}
        </MDInput>
      </MDBox>
      <MDButton
        size="small"
        color="success"
        disabled={busy || !pet || lvlFrom === "" || lvlTo === ""}
        onClick={submit}
      >
        Thêm
      </MDButton>
    </MDBox>
  );
}

AddMob.propTypes = {
  mapId: PropTypes.number.isRequired,
  pets: PropTypes.array.isRequired,
  archetypes: PropTypes.array.isRequired,
  busy: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
};

/**
 * Quái thường theo map (bảng petrealm_map_moblvl).
 * Sửa xong là đợt quái hồi sinh kế tiếp đã dùng dữ liệu mới, không cần khởi động lại.
 */
function MapMobs() {
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const ask = useConfirmChanges();
  const [onlyWithMobs, setOnlyWithMobs] = useState(true);
  const canEdit = useCan(PERMISSIONS.RATE_EDIT);
  const maps = useFetch(() => api.get("/api/admin/rates/map-mobs"), []);
  const options = useFetch(() => api.get("/api/admin/rates/map-mobs/options"), []);

  const pets = options.data?.pets || [];
  const archetypes = options.data?.archetypes || [];

  const shown = useMemo(() => {
    const list = maps.data || [];
    return onlyWithMobs ? list.filter((m) => m.rows.length > 0 || m.locations > 0) : list;
  }, [maps.data, onlyWithMobs]);

  const save = async (row, draft) => {
    const yes = await ask({
      title: `Sửa ${row.petName || `loài #${row.petId}`}`,
      before: row,
      after: draft,
      fields: [
        ["lvlFrom", "Cấp từ"],
        ["lvlTo", "Cấp đến"],
        ["archetypeId", "Nguyên mẫu"],
      ],
      notes: ["Đợt quái hồi sinh kế tiếp trên map sẽ dùng dữ liệu mới."],
    });
    if (!yes) return;
    const res = await run(
      () =>
        api.put("/api/admin/rates/map-mobs", {
          mapId: row.mapId,
          petId: row.petId,
          lvlFrom: Number(draft.lvlFrom),
          lvlTo: Number(draft.lvlTo),
          archetypeId: Number(draft.archetypeId),
          origLvlFrom: row.lvlFrom,
          origLvlTo: row.lvlTo,
        }),
      "Đã lưu, đợt quái hồi sinh kế tiếp sẽ dùng dữ liệu mới"
    );
    if (res.ok) maps.reload();
  };

  const remove = async (row) => {
    const yes = await confirm({
      title: "Bỏ loài quái khỏi map",
      message: `Bỏ ${row.petName || `loài #${row.petId}`} (${lvlText(
        row.lvlFrom,
        row.lvlTo
      )}) khỏi map? Từ đợt hồi sinh kế tiếp loài này không ra ở map đó nữa.`,
      color: "error",
      confirmText: "Bỏ khỏi map",
    });
    if (!yes) return;
    const res = await run(
      () =>
        api.del(
          `/api/admin/rates/map-mobs?mapId=${row.mapId}&petId=${row.petId}` +
            `&lvlFrom=${row.lvlFrom}&lvlTo=${row.lvlTo}`
        ),
      "Đã xoá"
    );
    if (res.ok) maps.reload();
    return res.ok;
  };

  const add = async (body) => {
    const res = await run(() => api.post("/api/admin/rates/map-mobs", body), "Đã thêm");
    if (res.ok) maps.reload();
    return res.ok;
  };

  return (
    <MDBox>
      <Alert severity="info" sx={{ mb: 2 }}>
        Map chỉ sinh quái khi có <strong>cả loài quái lẫn điểm sinh quái</strong>. Sửa ở đây{" "}
        <strong>ăn ngay</strong> từ đợt quái hồi sinh kế tiếp, không cần khởi động lại máy chủ —
        riêng <strong>điểm sinh quái</strong> thì phải sửa trong database và khởi động lại. Cấp quái
        bốc ngẫu nhiên theo kiểu <strong>loại trừ cấp đến</strong>, nên khai 56–60 thì thực tế chỉ
        ra cấp 56–59.
      </Alert>

      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDButton
          size="small"
          color={onlyWithMobs ? "info" : "secondary"}
          variant={onlyWithMobs ? "gradient" : "outlined"}
          onClick={() => setOnlyWithMobs((v) => !v)}
        >
          {onlyWithMobs ? "Đang ẩn map không có chỗ sinh quái" : "Đang hiện tất cả map"}
        </MDButton>
      </MDBox>

      {(maps.loading || options.loading) && <MDTypography variant="button">Đang tải…</MDTypography>}

      <Grid container spacing={3}>
        {shown.map((m) => (
          <Grid item xs={12} lg={6} key={m.mapId}>
            <PageCard
              title={`${m.mapName} (#${m.mapId})`}
              subtitle={
                m.locations === 0
                  ? "Chưa có điểm sinh quái — map này không sinh quái dù có khai loài"
                  : `${m.locations} điểm sinh quái · ${m.rows.length} loài`
              }
              color={m.locations === 0 ? "warning" : "info"}
            >
              <MDBox pt={2}>
                {m.rows.length === 0 && (
                  <MDTypography variant="button" color="text">
                    Chưa khai loài quái nào.
                  </MDTypography>
                )}
                {m.rows.map((row) => (
                  <MobRow
                    key={`${row.petId}-${row.lvlFrom}-${row.lvlTo}`}
                    row={row}
                    archetypes={archetypes}
                    busy={busy}
                    onSave={(draft) => save(row, draft)}
                    onDelete={() => remove(row)}
                  />
                ))}
                {canEdit && (
                  <>
                    <Divider />
                    <AddMob
                      mapId={m.mapId}
                      pets={pets}
                      archetypes={archetypes}
                      busy={busy}
                      onAdd={add}
                    />
                  </>
                )}
              </MDBox>
            </PageCard>
          </Grid>
        ))}
      </Grid>
    </MDBox>
  );
}

export default MapMobs;
